// Identify a dotted column reference at a Monaco cursor and decide whether
// the relation + column it names exist in the current Object Explorer tree.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 6 (column).
//
// For explicit-qualified column nav (`users.email`, `schema.users.email`,
// `database.schema.users.email`) the OE tree alone is enough: the dotted
// parser hands us the parts, the OE tree tells us whether the relation +
// column exist under the current connection (searching the NAMED database's
// subtree when the chain is database-qualified, not just the tab's), and we
// shape the return as the resolver's `IdentifierBinding` so the definition
// provider can dispatch uniformly with future kinds.
//
// Alias-qualified columns (`u.email` where `u` is `FROM users u`) round-trip
// through an injected `AliasResolver` (built lazily by the caller via
// `bufferAliasResolver`). A bare 1-segment prefix asks the alias resolver
// FIRST: a declared alias is a statement-local binding that shadows every
// relation, so when one exists the OE-tree lookup runs against the alias
// TARGET and nothing else — a prefix the statement declared as an alias is
// never a table reference, even if the catalog happens to hold a table of
// that name. The resolver answers null when no such alias is declared, and
// the prefix is then looked up as a relation directly.
//
// Bare 1-part columns (`email` with no dotted prefix) still return `null`
// here — they need scope.reachableTables walking and stay deferred until a
// per-Monaco SqlResolverService is wired.
//
// Returns `{ kind: 'column', relation: ReachableTable, column: QualifiedName }`
// shaped per the resolver's union, so consumers can narrow uniformly.

import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import {
  findDottedChainStart,
  makeQualifiedName,
  parseDottedChain,
} from '../../sql-resolver/dotted/dottedParts';
import type { DatabaseExplorerNode } from '../../explorer/models/DatabaseTreeModels';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import type { DatabaseEngineId } from '@/core/engines';
import type { IdentifierBinding, QualifiedName } from '../types';

/** Slice 6 returns more than the bare resolver `IdentifierBinding`: callers
 *  also need to know whether the relation is a table or a view so the
 *  navigation can route to Table Designer (table) or to a CREATE-text query
 *  tab (view). The `binding` field stays in the resolver's shape for forward
 *  compatibility with later slices. */
export type ColumnBindingHit = {
  readonly binding: Extract<IdentifierBinding, { kind: 'column' }>;
  readonly relationKind: 'table' | 'view';
  /** The CATALOG spelling of the database the TYPED relation prefix bound to
   *  (`Sales.dbo.ARD.Id` matching a `sales` node → `sales`; via an alias, the
   *  database the alias's FROM entry bound to), `undefined` when the user
   *  didn't qualify by database. Distinct from
   *  `binding.relation.qualified.database`, which is where the OE tree
   *  HAPPENED to find the relation; find-references routing may only trust
   *  this one (see `find-references/referenceTargetAtCursor.ts`). */
  readonly explicitDatabase?: string;
};
import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { catalogRelationRef, foldOeComponent, inferOeComponentQuoting } from '../oeIdentifier';
import { defaultSchemaForEngine } from './externalNavigation';
import { extendCursorToIdentEnd } from './cursorIdent';
import {
  catalogComponent,
  catalogDatabaseForTyped,
  matchesTypedQualification,
  objectComponentEquals,
  oeDatabaseIdentity,
  preferContextDatabase,
  preferExactTypedComponents,
  typedComponent,
} from './oeTreeQualification';
import type { AliasResolver } from './bufferAliasResolver';

export type FindColumnDeps = {
  readonly engineId: DatabaseEngineId;
  readonly connectionId: string | null;
  readonly database: string | null;
  /** Allows tests to inject a tree without using the live Zustand store. */
  readonly getTree?: () => readonly DatabaseExplorerNode[];
  /** Resolves an unqualified prefix (e.g. `u` in `u.email`) against the
   *  cursor-enclosing statement's FROM/JOIN aliases. Returns `null` when no
   *  such alias exists; the caller falls back to the direct table lookup. */
  readonly resolveAlias?: AliasResolver;
};

/** Resolves an `IdentifierBinding` of `kind: 'column'` for the qualified
 *  column name at `offset`. Returns `null` when the chain has fewer than two
 *  parts (deferred to alias/scope-aware slices), when no candidate relation
 *  exists in the OE tree, or when the relation does not own a column with the
 *  named identifier. The returned `relation.qualified` is the OE-tree's
 *  view-of-the-world (so a folded `users` cursor on a case-insensitive engine
 *  yields the source-cased OE node `Users`); the column QualifiedName is
 *  built from the cursor's source token. */
export function findColumnBindingAtCursor(
  source: string,
  offset: number,
  deps: FindColumnDeps,
): ColumnBindingHit | null {
  if (!deps.connectionId) return null;
  const sqlEngine = databaseEngineIdToSqlEngine(deps.engineId) ?? 'mssql';
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };

  const cursorAtEnd = extendCursorToIdentEnd(source, offset);
  const chainStart = findDottedChainStart(source, cursorAtEnd, options);
  const chain = parseDottedChain(source, chainStart, cursorAtEnd, options);
  if (!chain || chain.parts.length < 2) return null;

  // Last part is the column; up to THREE parts before it form the relation
  // (table / schema.table / database.schema.table) — mirrors the full
  // 3-part relation `identifierAtCursor.ts` / `routineAtCursor.ts` keep via
  // `chain.parts.slice(-3)`, so a fully 4-part `database.schema.table.column`
  // chain (e.g. `MandTob.dbo.ARD.Id` typed from a `master` tab) resolves
  // under the NAMED database instead of silently dropping the leading
  // segment and missing. MySQL's `db.table.column` is only a 3-part chain
  // whose first segment IS the schema (MySQL has no separate schema concept)
  // — that shape is already covered by the 2-part case below and unaffected
  // by this change.
  const colPart = chain.parts[chain.parts.length - 1];
  const relParts = chain.parts.slice(-4, -1); // 1, 2, or 3 parts
  const relQualified = makeQualifiedName(relParts);
  const colQualified = makeQualifiedName([colPart]);

  const treeData = (deps.getTree ?? readLiveTree)();
  // A DECLARED alias shadows every relation, so it is asked FIRST: within its
  // statement `u` IS the aliased relation, whatever the catalog also happens
  // to call `u` — in the tab's database or a foreign one. The declaration
  // itself proves `u` is not a table reference, so an alias target the tree
  // has not loaded yet resolves to NOTHING rather than to a same-named table
  // (which navigation and Rename would then rewrite instead of the relation
  // the user meant). `resolveAlias` returns null when the statement declares
  // no such alias (`FROM u` declares none) — the direct lookup then stands.
  const aliasTarget =
    relParts.length === 1 ? (deps.resolveAlias?.(relQualified.foldedName) ?? null) : null;
  // The database the user SPELLED OUT, in the tree's catalog spelling — from
  // the chain itself, or from the FROM entry an alias points at. Never a
  // database only the tree's fallback chose; see
  // `ColumnBindingHit.explicitDatabase`.
  const typedDatabase = aliasTarget ? aliasTarget.database : relQualified.database;
  const via: 'direct' | 'alias' = aliasTarget ? 'alias' : 'direct';
  // One lookup for both paths — the alias TARGET when one was declared, else
  // the typed prefix. Either way it prefers the TAB's database over a foreign
  // same-named relation and only then falls back across databases (the rank
  // `preferContextDatabase` applies inside `lookupRelationWithColumn`, shared
  // with the table/view and routine dispatches — `oeTreeQualification.ts`).
  const relation = lookupRelationWithColumn(
    treeData,
    deps,
    aliasTarget ?? relQualified,
    colQualified,
    sqlEngine,
  );
  if (!relation) return null;
  const explicitDatabase = catalogDatabaseForTyped(typedDatabase, relation.qualified.database);

  return {
    explicitDatabase,
    binding: {
      kind: 'column',
      relation: { qualified: relation.qualified, via },
      column: {
        ...colQualified,
        // The CATALOG's spelling, not the cursor's. A rename names the old
        // column from here (`rename/renameSchemaAlter.ts`), and a quoted host
        // relation delimits it — so the typed `id` for a catalog `ID` became
        // `RENAME COLUMN "id"`, a column Oracle never made (ORA-00904;
        // PostgreSQL: `column "ID" does not exist`). The folded half is left
        // alone: it is the MATCHING key, and the lookup below only matched
        // because the two already agree under this engine's rules.
        name: relation.columnName,
        schema: relation.qualified.schema,
        database: relation.qualified.database,
        foldedSchema: relation.qualified.foldedSchema,
        foldedDatabase: relation.qualified.foldedDatabase,
      },
    },
    relationKind: relation.objectType,
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

type RelationCandidate = {
  readonly objectType: 'table' | 'view';
  readonly qualified: QualifiedName;
  /** The CATALOG spelling of the column the cursor token matched — `ID` for a
   *  typed `id` on Oracle. The spelling a DDL statement has to name; the typed
   *  one only ever matched it through the engine's folding rules. */
  readonly columnName: string;
};

function lookupRelationWithColumn(
  treeData: readonly DatabaseExplorerNode[],
  deps: FindColumnDeps,
  relation: QualifiedName,
  column: QualifiedName,
  engine: SqlEngine,
): RelationCandidate | null {
  // Case-INSENSITIVE pre-filters (here and for the column below): a cheap
  // superset, with the precise engine-aware equality applied once per candidate
  // — the same two-step the table/view and routine dispatches use.
  const relName = relation.foldedName.toLowerCase();
  // Both spellings of every typed component; which half an exact comparison
  // reads is per engine (`oeTreeQualification.ts`, FOLDED vs SPELLED).
  const typedDatabase = typedComponent(relation.foldedDatabase, relation.database);
  const typedSchema = typedComponent(relation.foldedSchema, relation.schema);
  const typedRelation = typedComponent(relation.foldedName, relation.name);
  const typedColumn = typedComponent(column.foldedName, column.name);
  // Permissive schema matching mirrors `findRevealNodeId` (Slice 5 table/view
  // dispatch): when the user-typed identifier has no schema part, accept any
  // OE-tree schema; only reject when both sides are non-empty and disagree.
  // Some engines project schemas the column dispatch's `defaultSchemaForEngine`
  // doesn't predict (mysql current-database is `''` not `dbo`; sqlite uses
  // `main`), so a strict default-vs-tree compare blocks unqualified F12 there.
  const userTypedSchema = relation.foldedSchema?.toLowerCase();

  // We collect all matching table+view nodes and filter by the column existence
  // afterwards so a same-named view in another schema doesn't shadow the
  // intended hit.
  const candidates: {
    relType: 'table' | 'view';
    db: string | undefined;
    schema: string | undefined;
    name: string;
    columns: readonly string[];
  }[] = [];

  walk(treeData, undefined);

  const matching = candidates.filter((c) => {
    // Components the chain SPELLED OUT are binding — the same gate the
    // table/view and routine dispatches use (`oeTreeQualification.ts`), so all
    // three agree on when a cross-database or cross-schema match is allowed.
    if (
      !matchesTypedQualification(
        { database: c.db, schema: c.schema },
        {
          typedDatabase,
          typedSchema,
          contextDatabase: deps.database,
          engineId: deps.engineId,
          connectionId: deps.connectionId,
        },
      )
    ) {
      return false;
    }
    // The relation's own name and the COLUMN name take the same engine-aware
    // equality as the schema: SQL Server compares both in the holding
    // DATABASE's collation (so a `_CS_` database really has `T.Id` next to
    // `T.id`), pg/oracle byte-exactly after folding. Merging them accepted a
    // column the relation does not have and navigated to the wrong one of two.
    const identity = oeDatabaseIdentity(deps.engineId, deps.connectionId, c.db, deps.database);
    if (c.name.toLowerCase() !== relName) return false;
    if (
      !objectComponentEquals(
        deps.engineId,
        deps.connectionId,
        identity,
        typedRelation,
        c.name,
        'relation',
      )
    ) {
      return false;
    }
    return c.columns.some((cn) =>
      objectComponentEquals(deps.engineId, deps.connectionId, identity, typedColumn, cn, 'column'),
    );
  });
  // The tab's database outranks every other one: a bare `users.email` typed in
  // `B` means B's `users`, even when the tree lists A's same-named relation
  // first (`oeTreeQualification.ts` — the table/view and routine dispatches
  // rank identically). The default-schema preference below then applies WITHIN
  // that rank.
  const ranked = preferContextDatabase(
    matching,
    deps.engineId,
    deps.connectionId,
    deps.database,
    (c) => c.db,
  );
  if (ranked.length === 0) return null;
  // When the user didn't qualify with a schema, prefer the engine's default
  // schema so a same-named relation in `dbo` (or `public` etc.) wins over an
  // alphabetically-earlier sibling. Falls back to the whole rank when the
  // default schema isn't among the candidates (e.g. mysql/sqlite cases).
  // The equality is the SAME engine-aware one the gate above uses, read under
  // each candidate's own database identity — a blanket `.toLowerCase()` folded
  // `DBO` into `dbo` after the policy was known, so in a `_CS_` database a bare
  // `users.email` took whichever of `DBO.users` / `dbo.users` the tree listed
  // first even though only one of them is in the default schema.
  const defaultSchema = catalogComponent(defaultSchemaForEngine(deps.engineId));
  const schemaMatched = userTypedSchema
    ? ranked
    : ranked.filter((c) =>
        objectComponentEquals(
          deps.engineId,
          deps.connectionId,
          oeDatabaseIdentity(deps.engineId, deps.connectionId, c.db, deps.database),
          defaultSchema,
          c.schema ?? '',
          'schema',
        ),
      );
  const pool = schemaMatched.length > 0 ? schemaMatched : ranked;
  // Last, among equals, the relation spelled exactly as the user typed it —
  // the probe-free tie-break for a case-insensitive (or not-yet-probed) policy
  // holding both `dbo.T` and `dbo.t`.
  const chosen = preferExactTypedComponents(
    pool,
    { schema: relation.schema, name: relation.name },
    { schema: (c) => c.schema, name: (c) => c.name },
  )[0];
  // Fold the host relation's OE catalog components engine-aware so the identity
  // matches the walker's recorded `hostTable` (oracle unquoted → UPPER, pg →
  // lower, quoted names verbatim). The old blanket `.toLowerCase()` made
  // `filterToContainingTable`'s `relationIdentitiesEqual` compare `accounts`
  // vs `ACCOUNTS` on oracle and silently drop the column rewrite (CK-B P1-1).
  return {
    objectType: chosen.relType,
    columnName:
      chosen.columns.find((cn) =>
        objectComponentEquals(
          deps.engineId,
          deps.connectionId,
          oeDatabaseIdentity(deps.engineId, deps.connectionId, chosen.db, deps.database),
          typedColumn,
          cn,
          'column',
        ),
      ) ?? column.name,
    qualified: {
      name: chosen.name,
      schema: chosen.schema,
      database: chosen.db,
      raw: catalogRelationRef(engine, chosen.schema, chosen.name),
      quoted: inferOeComponentQuoting(engine, chosen.name),
      foldedName: foldOeComponent(engine, chosen.name),
      foldedSchema: chosen.schema ? foldOeComponent(engine, chosen.schema) : undefined,
      foldedDatabase: chosen.db ? foldOeComponent(engine, chosen.db) : undefined,
    },
  };

  function walk(
    nodes: readonly DatabaseExplorerNode[] | undefined,
    currentDb: string | undefined,
  ): void {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === 'connection') {
        const meta = node.metadata as { id?: string } | undefined;
        if (meta?.id !== deps.connectionId) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, undefined);
        continue;
      }
      if (node.type === 'database') {
        const meta = node.metadata as { name?: string } | undefined;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, meta?.name);
        continue;
      }
      if (node.type === 'table' || node.type === 'view') {
        const meta = node.metadata as
          | { objectDetails?: { name?: string; schema?: string } }
          | undefined;
        const od = meta?.objectDetails;
        if (od?.name) {
          candidates.push({
            relType: node.type,
            db: currentDb,
            schema: od.schema,
            name: od.name,
            columns: collectColumnNames(node.children as readonly DatabaseExplorerNode[] | undefined),
          });
        }
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
        continue;
      }
      walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
    }
  }
}

function collectColumnNames(
  children: readonly DatabaseExplorerNode[] | undefined,
): readonly string[] {
  if (!children) return [];
  const out: string[] = [];
  for (const child of children) {
    if (child.type === 'columns-folder') {
      const grand = child.children as readonly DatabaseExplorerNode[] | undefined;
      for (const col of grand ?? []) {
        if (col.type === 'column') {
          const meta = col.metadata as { columnName?: string } | undefined;
          if (meta?.columnName) out.push(meta.columnName);
        }
      }
      continue;
    }
    // Some seed paths attach column nodes directly under the relation (e.g.
    // hand-built test trees). Tolerate that shape too.
    if (child.type === 'column') {
      const meta = child.metadata as { columnName?: string } | undefined;
      if (meta?.columnName) out.push(meta.columnName);
    }
  }
  return out;
}

function readLiveTree(): readonly DatabaseExplorerNode[] {
  return useDatabaseStore.getState().treeData;
}
